import { Fragment, jsx, jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from "react";
import { CompanyKey } from "./components/CompanyKey/CompanyKey.js";
import { GracePeriodAlert } from "./components/GracePeriodAlert/GracePeriodAlert.js";
import { LoginFooter } from "./components/LoginFooter/LoginFooter.js";
import { LoginFormDivider } from "./components/LoginFormDivider/LoginFormDivider.js";
import { LoginSSOButton } from "./components/LoginSSOButton/LoginSSOButton.js";
import { PasswordField } from "./components/PasswordField/PasswordField.js";
import { RememberMe } from "./components/RememberMe/RememberMe.js";
import { UidField } from "./components/UidField/UidField.js";
import { UsernameField } from "./components/UsernameField/UsernameField.js";
import { useAutoFocusEmptyField } from "./hooks/useAutoFocusEmptyField.js";
import { loginForm, loginFormInitialValues } from "./loginFormSetup.js";
import { TextsProvider } from "./texts/texts.js";
function LoginForm(props) {
  const {
    texts,
    onInitialize,
    initialValues,
    onFormApi,
    companyKey,
    onNotYourCompanyKeyClick,
    onLogin,
    autoFocus,
    showUid = false,
    customSlot,
    hideRememberMe = false,
    sx,
    testId = "login-form-root",
    className = "",
    componentProps,
    fieldProps,
    variant = "standard",
    showLoader,
    loaderColor,
    ...tidalFormProps
  } = props;
  const rNotifyWrongConfig = useRef(true);
  useEffect(() => {
    if (!rNotifyWrongConfig.current) return;
    if (!onInitialize) return;
    if (!initialValues) return;
    rNotifyWrongConfig.current = false;
    console.warn(
      "[Login Form] `onInitialize` and `initialValues` are mutually exclusive. `initialValues` will be ignored."
    );
  }, [onInitialize, initialValues]);
  return /* @__PURE__ */ jsx(TextsProvider, { texts, children: /* @__PURE__ */ jsxs(
    loginForm.Form,
    {
      disableScroll: true,
      fieldsPadding: 0,
      fieldsSpacing: 2,
      sx,
      className,
      ...tidalFormProps,
      testId,
      footer: /* @__PURE__ */ jsx(FooterComponent, { ...props }),
      onFormApi,
      onInitialize: () => {
        if (onInitialize) {
          return onInitialize();
        }
        return {
          initialValues: Object.assign({}, loginFormInitialValues, initialValues)
        };
      },
      onSubmit: async (formState, formValues, api, formEvent) => {
        await onLogin(formState, "standard");
      },
      children: [
        /* @__PURE__ */ jsx(CompanyKeyComponent, { ...props }),
        /* @__PURE__ */ jsx(SSOLoginButton, { ...props }),
        /* @__PURE__ */ jsx(FormDividerComponent, { ...props }),
        /* @__PURE__ */ jsx(GracePeriodComponent, { ...props }),
        /* @__PURE__ */ jsx(LoginFormFields, { ...props })
      ]
    }
  ) });
}
function LoginFormFields(props) {
  const {
    autoFocus,
    showUid = false,
    customSlot,
    hideRememberMe = false,
    componentProps,
    fieldProps,
    variant = "standard"
  } = props;
  const autoFocusField = useAutoFocusEmptyField(autoFocus, showUid);
  const showRememberMe = !hideRememberMe;
  const hideStandardVariant = variant === "sso";
  if (hideStandardVariant) return void 0;
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    showUid ? /* @__PURE__ */ jsx(UidField, { autoFocusField, componentProps: componentProps?.uid, fieldProps: fieldProps?.uid }) : null,
    /* @__PURE__ */ jsx(
      UsernameField,
      {
        autoFocusField,
        componentProps: componentProps?.username,
        fieldProps: fieldProps?.username
      }
    ),
    /* @__PURE__ */ jsx(
      PasswordField,
      {
        autoFocusField,
        componentProps: componentProps?.password,
        fieldProps: fieldProps?.password
      }
    ),
    customSlot,
    showRememberMe ? /* @__PURE__ */ jsx(RememberMe, { componentProps: componentProps?.rememberMe, fieldProps: fieldProps?.rememberMe }) : null
  ] });
}
function FooterComponent(props) {
  const {
    autoFocus,
    showUid = false,
    componentProps,
    fieldProps,
    variant = "standard",
    showLoader,
    loaderColor
  } = props;
  const showStandardVariant = variant !== "sso";
  const showBothVariants = variant === "sso+standard";
  return showStandardVariant ? /* @__PURE__ */ jsx(
    LoginFooter,
    {
      showUid,
      footerProps: fieldProps?.footerProps,
      autoFocus,
      buttonProps: {
        ...showBothVariants ? { color: "secondary" } : {},
        ...componentProps?.submitButton,
        ...fieldProps?.submitButton
      },
      showLoader,
      loaderColor
    }
  ) : void 0;
}
function CompanyKeyComponent(props) {
  const { companyKey, onNotYourCompanyKeyClick } = props;
  return companyKey ? /* @__PURE__ */ jsx(CompanyKey, { companyKey, onNotYourCompanyKeyClick }) : void 0;
}
function SSOLoginButton(props) {
  const { variant = "standard", onLogin, showLoader, loaderColor } = props;
  const [disabled, setDisabled] = useState(false);
  const showSSOVariant = variant !== "standard";
  const autofocus = variant !== "sso";
  const icon = props.variant === "sso" || props.variant === "sso+standard" ? props.ssoIcon : void 0;
  async function onClick() {
    setDisabled(true);
    await onLogin({ uid: "", password: "", username: "" }, "sso");
    setDisabled(false);
  }
  const loading = showLoader ? disabled : false;
  return showSSOVariant ? /* @__PURE__ */ jsx(
    LoginSSOButton,
    {
      autoFocus: autofocus,
      icon,
      buttonProps: { onClick, disabled },
      loading,
      loaderColor
    }
  ) : void 0;
}
function FormDividerComponent(props) {
  const { variant = "standard" } = props;
  const showBothVariants = variant === "sso+standard";
  return showBothVariants ? /* @__PURE__ */ jsx(LoginFormDivider, {}) : void 0;
}
function GracePeriodComponent(props) {
  const gracePeriod = props.variant === "sso+standard" ? props.gracePeriod : void 0;
  const gracePeriodUnit = props.variant === "sso+standard" ? props.gracePeriodUnit ?? "day" : void 0;
  const showGracePeriod = gracePeriod !== void 0 && gracePeriod > 0;
  return showGracePeriod ? /* @__PURE__ */ jsx(GracePeriodAlert, { gracePeriod, gracePeriodUnit }) : void 0;
}
export {
  LoginForm
};
//# sourceMappingURL=LoginForm.js.map
